import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export type OperatorCompareTier = "hot" | "cold";
export type OperatorCompareBucket = "day" | "week" | "month";

export interface OperatorCompareRowResponse {
  operatorId: string;
  docs: number;
  avgKpm: number;
  avgCorrectionRate: number;
  avgTimePerFieldMs: number;
}

export interface OperatorCompareResponse {
  tier: OperatorCompareTier;
  bucket: OperatorCompareBucket;
  rows: OperatorCompareRowResponse[];
}

/**
 * Up to 4 operators — server returns 400 for 0 or >4.
 */
export function useOperatorCompare(
  operatorIds: string[],
  from?: string,
  to?: string,
  bucket?: OperatorCompareBucket,
) {
  const [data, setData] = useState<OperatorCompareResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ids = operatorIds.slice(0, 4).join(",");

  const fetchData = useCallback(async () => {
    if (!ids) {
      setIsLoading(false);
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("operatorIds", ids);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = `?${params.toString()}`;
      const result = await api.get<OperatorCompareResponse>(`/analytics/operator-compare${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load operator compare");
    } finally {
      setIsLoading(false);
    }
  }, [ids, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
